import { Injectable, signal, computed, effect } from '@angular/core';
import { fromEvent, merge, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class OfflineService {
  #isOnline = signal<boolean>(navigator.onLine);
  #onOnlineCallbacks: ((offlineStartTime: number | null) => void)[] = [];
  #offlineStartTime: number | null = null;

  isOnline = this.#isOnline.asReadonly();
  isOffline = computed(() => !this.#isOnline());

  constructor() {
    this.initOnlineStatusListener();
  }

  private initOnlineStatusListener(): void {
    if (typeof window === 'undefined') {
      return;
    }

    merge(
      of(navigator.onLine),
      fromEvent(window, 'online').pipe(map(() => true)),
      fromEvent(window, 'offline').pipe(map(() => false))
    ).subscribe((isOnline) => {
      const wasOffline = this.#isOnline() === false;
      this.#isOnline.set(isOnline);

      if (!isOnline && !wasOffline) {
        this.#offlineStartTime = Date.now();
      }

      if (isOnline && wasOffline) {
        const offlineStartTime = this.#offlineStartTime;
        this.#onOnlineCallbacks.forEach((callback) => callback(offlineStartTime));
        this.#offlineStartTime = null;
      }
    });
  }

  onOnline(callback: (offlineStartTime: number | null) => void): void {
    this.#onOnlineCallbacks.push(callback);
  }

  /**
   * Get the timestamp when the app went offline
   * Returns null if the app is currently online or never went offline
   */
  getOfflineStartTime(): number | null {
    return this.#offlineStartTime;
  }
}
